import React, { useRef } from "react";
import { Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import {
  getTextContentWithHeading,
  h1style,
  referenceTootKit,
  sectionStyle,
  h2style,
  h3style,
  paraStyle,
  pointStyle,
  getTextContentHeading,
  dimensionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import lockupImg from "../../../assets/Images/JudicialLockUp Images/Lock-UpImg.png";
import { FigureDesc56 } from "../../../utils/tmp/ImageDescription/JudicialLockupID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const LockUp = ({ goToSection }) => {
  const referencesRef = useRef(null);
  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: "NCMS (2012)",
    },
    {
      content:
        <><i>National Legal Services Authority v. Union of India</i> (2014) 5 SCC 438.</>,
    },
    {
      content: "NCMS (2012).",
    },
    {
      content:
        <>Central Prisoner Holding and Transport <i>(The Courthouse: A Guide to Planning & Design)</i> </>,
      link: "https://www.ncsc.org/courthouseplanning/security/central-prisoners",
      description:
        "<https://www.ncsc.org/courthouseplanning/security/central-prisoners> ",
    },
    {
      content: "Punjab & Haryana High Court Rules, chapter 27, s xvi(iii). ",
    },
  ];

  const lockUpTypes = {
    data: [
      <>
        <span style={{ fontWeight: "bold", color: "black" }}>
          Single Lock-up:{" "}
        </span>
        Separate lock-ups for individual undertrials charged with serious
        offences, ensuring greater security and segregation; and
      </>,

      <>
        <span style={{ fontWeight: "bold", color: "black" }}>
          Group Lock-up:{" "}
        </span>
        Separate lock-ups segregated by gender and age (with a segregated
        lock-up for juveniles), including persons belonging to the third gender.
        {reference(referenceData[1], 2)}
      </>,
    ],
    isOrdered: false,
  };

  const lockAreaOject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure that the lock-ups are
        <span style={{ color: "#DC002D" }}>
          {" "}
          secure temporary detention/holding facilities{" "}
        </span>
        within the Court building, away from the areas frequented by other users
      </>,
      <>
        Plan the location of lock-ups such that they are in
        <span style={{ color: "#DC002D" }}> proximity to the Courtrooms</span>,
        to minimise delays during the scheduled appearances of the Accused in
        the Court; and
      </>,

      <>
        Ensure that the
        <span style={{ color: "#DC002D" }}> lock-ups are humane spaces</span>,
        and consider the needs of diverse Accused Persons.
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Accused Persons/Undertrials",
      "Advocates",
      "Police Investigators (PI)",
      "Security Staff",
    ],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The lock-up facilities must be directly accessible through a segregated
        path from the designated parking space for prison vans used to transport
        the Accused. For further details regarding segregated pathways for the
        Accused, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection("spacePlanningCirculation", "entrancesAndExits")
          }
        >
          Section 1.2.{" "}
        </span>
      </>,

      "The lock-up facilities must have direct access, or be in close proximity to secure corridors, elevators and stairs leading directly to the Accused waiting areas and the Courtrooms. This would reduce unauthorised interaction with the general public as well as the witnesses, while maintaining the safety and constitutional rights of the Accused.",

      "The lock-ups must have separate guarded entry/exit points through which the undertrials may be escorted to the relevant waiting areas and the Courtroom.",

      "The lock-ups must be located adjacent to the Visiting Room for the Accused and the Police Inspectors’ Room, to enable the Accused to meet and communicate with their lawyers or family/friends in a secure manner.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The design of the lock-ups, corridors leading up to the lock-ups, and related functions must take into consideration the safety of all users of the space. ",
        "Wherever possible, depending on the size of the District Court, a separate lock-up building or a wing may be provided within the Court Complex. This must be immediately adjacent to a separate entry to the building, with adequate enclosure for jail vans and police vehicles.",

        "Alternatively, the lock-up facilities may be provided on the ground floor or basement of the building.",

        <>
          Designated parking spaces must be provided for the vehicles escorting
          the Accused Persons. For further details of the parking area, please
          refer to{" "}
          <span
            style={sectionStyle}
            onClick={() =>
              goToSection("spacePlanningCirculation", "prisonVans")
            }
          >
            Section 1.3.E.
          </span>
        </>,
        "All corridors must be wide enough to prohibit Accused Persons from grabbing officers or other Accused persons as well to ensure that the space is not claustrophobic and allows adequate two-way passage. ",

        "Provision must be made for accessible, clean, and well-maintained sanitary provisions within the lock-ups. ",

        "The lock-up doors must have a wide grill opening to ensure that food and water may be passed inside in a sanitary and orderly manner. ",

        "Secure drinking water facilities must be provided within the lock-ups. ",

        <>
          Lock-ups must be equipped with CCTV cameras (both PTZ and Fixed
          cameras) for surveillance systems with unobstructed direct visibility,
          and concealed light fixtures preventing misuse. For further details,
          please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() => goToSection("annexes", "HeadingAnnexure3")}
          >
            Annexure 3.
          </span>
        </>,

        "The corridors leading up to the lock-ups must be monitored 24/7 with CCTV cameras and trigger alarms, especially for loitering/unattended baggage.",

        "The design of the lock-up must be in such a manner that security personnel are able to view the entire perimeter of all the cells. ",

        "Adequate seating must be provided for accompanying security staff. ",
      ],
    },
    {
      heading: "Lock-up Cells",
      items: [
        <>
          Each lock-up must be built to accommodate not more than 15 to 20
          prisoners at a time.{reference(referenceData[2], 3)}
        </>,
        "Adequate, separate lock-up facilities must be made available for segregation by age and gender.",

        <>
          Juveniles must not be held in close proximity to adults. If, in some
          circumstances, juveniles are required to be in close contact with
          adults, additional sight and sound separation must be ensured.
          {reference(referenceData[3], 4)}
        </>,

        "Separate single lock-ups must be provided for individuals charged with serious offences, including individuals with criminal histories. ",

        <>
          A Lock-up may not be less than{" "}
          <span style={dimensionStyle}>648 cubic feet</span> of air space and{" "}
          <span style={dimensionStyle}>36 feet </span> of floor area per
          prisoner. Lateral ventilation of the cell, exclusive of the iron bars
          and door jambs, must not be less than{" "}
          <span style={dimensionStyle}>10 square feet</span>.
          {reference(referenceData[4], 5)}
        </>,

        "The lock-up facilities must be reinforced with a solid ceiling in the cells to ensure enhanced security.",

        "The cell must be well-ventilated and equipped with a sufficient number of vandal-resistant, fixed furniture to ensure adequate seating space for the undertrials, as well as concealed lighting fixtures.",

        "All lock-ups must be built to ensure accessible turning space, with doors, grab bars, accessible drinking water facilities, and other appropriate facilities.",

        "All spaces within the cell must be periodically cleaned and well-maintained. ",
      ],
    },
    {
      heading: "Washrooms within the Lock-up",
      items: [
        "Every cell must have its own closed, functional, hygienic washroom facilities with covered drains and built-in toilet cubicles, to ensure privacy and hygiene. ",
        <>
          The washrooms must be accessible to diverse user groups and have
          provision for adequate running water. For further details of
          accessible washrooms, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() => goToSection("sanitaryProvisions", "washrooms")}
          >
            Section 3.1.A.
          </span>
        </>,
      ],
    },
    {
      heading: "Office Space for Accompanying Police Inspectors",
      items: [
        "Adequate office space must be provided outside the lock-ups for accompanying police inspectors, as well as security staff charged with manning the lock-ups.",
        "The office space must have sufficient storage facilities and ergonomic furniture.",
        "It must also have access to dedicated sanitary provisions and drinking water facilities. ",
      ],
    },
    {
      heading: "Other facilities",
      items: [
        "Private and soundproof interview booths with security glazing must be provided in close proximity to the lock-up facilities, to facilitate confidential conversations of the Accused with their Advocates and family members/friends.",

        <>
          Provisions must be made for the Advocates and family members/friends
          to be able to enter the interview booths from the public area without
          entering the secured area. For further details on the design of the
          Visiting Room, please refer to
          <span
            onClick={() => goToSection("visitationRoom", "headingVistingRoom")}
            style={sectionStyle}
          >
            {" "}
            Section 2.7.B.
          </span>
        </>,
      ],
    },
  ];

  const referenceHtml = (stringsArray) => {
    return (
      <ol>
        {stringsArray?.map((text, index) => (
          <li style={{ ...pointStyle, paddingLeft: "10px" }} key={text.content}>
            {text.content}
            {text?.link && (
              <a
                style={{
                  color: "#DC002D",
                  textDecoration: "none",
                }}
                href={text.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text.description}
              </a>
            )}
          </li>
        ))}
      </ol>
    );
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACESS OF THE COURT COMPLEX
        </h2>
        <h3 id="HeadingLockups" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.7. </span>
          Facilities for Accused Persons
        </h3>
        <h4 id="lockups" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.7.</span>A. Lock-ups
        </h4>
        <p style={paraStyle}>
          The lock-up facilities in District Courts serve as secure holding
          areas, separate from public spaces, designed to temporarily detain or
          hold Accused individuals before and after their Court appearances.
          These spaces must be designed with a focus on security, efficiency,
          separation from other Court users and the public, and adherence to
          constitutional rights.
        </p>
        <p style={paraStyle}>
          All District Courts must have an adequate number of lock-up facilities
          to accommodate the Accused and undertrial prisoners visiting the
          Court. The lock-up facilities must be segregated by gender, age of the
          Accused, and severity of the crime, and must be designed to be humane
          and accommodating of all users.
        </p>
        <p style={paraStyle}>
          Two types of lock-ups must be provided:
          {reference(referenceData[0], 1)}
        </p>
        {getTextContentWithHeading(lockUpTypes)}
        {getTextContentHeading(lockAreaOject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}
        <CaptionWithBorders1
          image={[lockupImg]}
          isZoom={true}
          caption={"Figure 56: Typical floor plan for a lock-up"}
          zoomImgStyle={{ height: "600px" }}
          imageDescription={FigureDesc56}
        />
        <h2 ref={referencesRef} id="lockupsRef" style={referenceStyle}>
          <i>References</i>
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default LockUp;
